import { forwardRef, type ComponentPropsWithoutRef } from 'react'

import { cn } from '../../../utils/cn'
import { Button, type ButtonProps } from '../../atoms/button'
import { Icon } from '../../atoms/icon'
import { textVariants } from '../../atoms/text/text'

import { RuleSettingsSvg } from './rule-settings-svg'

export type CustomerTagType = 'system' | 'custom'

/* Figma: a 26px pill. Pinned rather than derived so the row keeps its height
   when caption-lg's line-height steps up at ≥1920px, and so the add button
   can share the number. */
const HEIGHT_CLASSES = 'h-[26px]'

const ROOT_CLASSES = cn(
  textVariants({ variant: 'caption-lg' }),
  HEIGHT_CLASSES,
  'bg-branding-white-50 border-surface-300 text-surface-title-50 inline-flex items-center gap-1 whitespace-nowrap rounded-full border py-1 pl-2 pr-3'
)

/* A 12px well: the 8px dot sits inside a ring of the same colour at 20%
   alpha, so the one CSS variable paints both. */
const COLOR_WELL_CLASSES =
  'relative size-3 shrink-0 rounded-full bg-[var(--customer-tag-color)]/20'
const COLOR_DOT_CLASSES =
  'absolute inset-0.5 rounded-full bg-[var(--customer-tag-color)]'

/* Focus treatment shared with the Toast close and MarketingBanner dismiss.
   Interim: the 18px glyph is sized here, on the button, and the Icon inherits
   it. Apps that also load acqua-ui's theme get its utility-layer
   `.material-symbols-rounded { font-size: inherit }` after our utilities, so a
   size set on the Icon itself loses. Once that rule moves to acqua-ui's base
   layer (where acqua keeps its own), the Icon can carry the size again. */
const REMOVE_CLASSES =
  'text-surface-title-50 hover:text-surface-title-100 focus-visible:ring-primary-200 flex shrink-0 cursor-pointer rounded-full text-[18px]/none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'

export interface CustomerTagProps
  extends Omit<ComponentPropsWithoutRef<'span'>, 'children' | 'color'> {
  label: string
  /**
   * `system` tags are applied by rules and carry the rule icon; `custom`
   * tags are the partner's own and carry the colour they were given.
   */
  type: CustomerTagType
  /** Any CSS colour. Read by custom tags only. */
  color?: string
  /** Custom tags show the remove action unless told otherwise. */
  removable?: boolean
  onRemove?: () => void
}

export const CustomerTag = forwardRef<HTMLSpanElement, CustomerTagProps>(
  (
    { label, type, color, removable = true, onRemove, className, ...props },
    ref
  ) => {
    const isCustom = type === 'custom'

    return (
      <span
        ref={ref}
        data-slot='customer-tag'
        data-type={type}
        className={cn(ROOT_CLASSES, className)}
        {...props}
      >
        {isCustom ? (
          <span
            aria-hidden='true'
            className={COLOR_WELL_CLASSES}
            style={{ '--customer-tag-color': color } as React.CSSProperties}
          >
            <span className={COLOR_DOT_CLASSES} />
          </span>
        ) : (
          <RuleSettingsSvg />
        )}
        {label}
        {isCustom && removable && (
          <button
            type='button'
            aria-label={`Remove ${label}`}
            className={REMOVE_CLASSES}
            onClick={onRemove}
          >
            <Icon name='delete' aria-hidden='true' />
          </button>
        )}
      </span>
    )
  }
)
CustomerTag.displayName = 'CustomerTag'

export type CustomerTagPlaceholderProps = Omit<
  ComponentPropsWithoutRef<'span'>,
  'children'
> & {
  /** Copy for the empty state; Figma reads "Not tagged". */
  label?: string
}

/**
 * Stands in for the tag row when a customer has no tags yet: the custom pill
 * with a dashed border and a neutral well (Figma "not tagged").
 */
export const CustomerTagPlaceholder = forwardRef<
  HTMLSpanElement,
  CustomerTagPlaceholderProps
>(({ label = 'Not tagged', className, ...props }, ref) => (
  <CustomerTag
    ref={ref}
    type='custom'
    label={label}
    color='var(--color-surface-400)'
    removable={false}
    className={cn('border-dashed', className)}
    data-type='placeholder'
    {...props}
  />
))
CustomerTagPlaceholder.displayName = 'CustomerTagPlaceholder'

type CustomerTagButtonProps = Omit<
  ButtonProps,
  'variant' | 'size' | 'iconOnly' | 'children'
>

export type CustomerTagAddButtonProps = CustomerTagButtonProps

// The secondary button's own small square is 32px; the tag row is 26.
const ADD_BUTTON_CLASSES = cn(HEIGHT_CLASSES, 'rounded-full')

/** The "+" that opens tag management next to a customer's tags. */
export const CustomerTagAddButton = forwardRef<
  HTMLButtonElement,
  CustomerTagAddButtonProps
>(({ 'aria-label': ariaLabel = 'Add tag', className, ...props }, ref) => (
  <Button
    ref={ref}
    variant='secondary'
    size='sm'
    iconOnly
    aria-label={ariaLabel}
    className={cn(ADD_BUTTON_CLASSES, className)}
    {...props}
  >
    <Icon name='add' aria-hidden='true' />
  </Button>
))
CustomerTagAddButton.displayName = 'CustomerTagAddButton'

export interface CustomerTagOverflowButtonProps extends CustomerTagButtonProps {
  /** How many tags the row is hiding. */
  count: number
}

// Same control as the add button; `py-0` keeps sm's vertical padding from
// pushing the label past the 26px row.
const OVERFLOW_BUTTON_CLASSES = cn(ADD_BUTTON_CLASSES, 'py-0')

/** The "+N" that stands in for the tags a row does not have room for. */
export const CustomerTagOverflowButton = forwardRef<
  HTMLButtonElement,
  CustomerTagOverflowButtonProps
>(({ count, 'aria-label': ariaLabel, className, ...props }, ref) => (
  <Button
    ref={ref}
    variant='secondary'
    size='sm'
    aria-label={
      ariaLabel ?? `Show ${count} more ${count === 1 ? `tag` : `tags`}`
    }
    className={cn(OVERFLOW_BUTTON_CLASSES, className)}
    {...props}
  >
    +{count}
  </Button>
))
CustomerTagOverflowButton.displayName = 'CustomerTagOverflowButton'
